/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import _ from "underscore";
import birdata from "../api.js";
import util from "util/util.js";
// This request queue prevents tile requests from piling up as a user quickly moves around the map. With a naive approach, the map generates
// many requests which do not complete in time to be displayed to the user. Meanwhile, they are waiting for the old off-screen requests to
// complete before they can load what is on the screen. One approach would be to cancel requests when they are no longer needed, but this
// does not give us much control. The browser might have already sent many unnecessary requests to the server when it would have been better
// not to send them in the first place. Instead, we place all requests on a queue and limit the number of active concurrent requests. When
// the user moves the map, any no-longer necessary requests that haven't been sent to the server are removed from the queue. We don't cancel
// inflight requests at the moment, but this could be worth investigating later.

// TODO - move this to a utility class
var requestQueue = (function() {
	var q = []; // A queue of keys to process
	var map = {}; // A map of keys to promise-returning functions
	var runningRequests = 0;
	var maxConcurrentRequests = 5;

	function add(key, promiseFn) {
		// console.log("Adding request with key: " + key)
		map[key] = promiseFn;
		q.push(key);
		process();
	}

	function process() {
		// console.log("Processing queue");
		while(q.length > 0 && runningRequests < maxConcurrentRequests) {
			var key = q.shift();
			// console.log("Got key: " + key);
			if(key in map) {
				var promiseFn = map[key];
				delete map[key];
				// console.log("Starting request for key: " + key);
				var promise = promiseFn();
				runningRequests++;
				promise.finally(function() {
					// console.log("Got response for key: " + key);
					runningRequests--;
					process();
				});
			} else {
				// console.log("Key not in map: " + key);
			}
		}
	}

	function remove(key) {
		// console.log("Remove key: " + key);
		delete map[key];
	}

	function has(key) {
		return key in map;
	}

	function clear() {
		map = {};
		q = [];
	}

	return { add, remove, has, clear }
})();

// Unusual approach used here. A normal custom map layer can't recieve mouse events
// because it's in the wrong map pane. So instead, we create an OverlayView for each
// map tile and add it to the 'overlayMouseTarget' pane. In the map tile code, we just
// return an empty dummy div for each map tile.

function tileOverlayView(x, y, z, tileSize, el) {
	var self = new google.maps.OverlayView();

	if(!el) {
		el = document.createElement('div');
	}
	el.style.position = 'absolute';

	self.onAdd = function() {
		var panes = this.getPanes();
		panes.overlayMouseTarget.appendChild(el);
	};

	self.draw = function() {
		var overlayProjection = this.getProjection();
		var projection = this.getMap().getProjection();

		var sw = projection.fromPointToLatLng(
			new google.maps.Point(
				x * tileSize.width / Math.pow(2, z),
				(y + 1) * tileSize.height / Math.pow(2, z)));

		var ne = projection.fromPointToLatLng(
			new google.maps.Point(
				(x + 1) * tileSize.width / Math.pow(2, z),
				y * tileSize.height / Math.pow(2, z)));

		sw = overlayProjection.fromLatLngToDivPixel(sw);
		ne = overlayProjection.fromLatLngToDivPixel(ne);

		el.style.left = sw.x + 'px';
		el.style.top = ne.y + 'px';
		el.style.width = (ne.x - sw.x) + 'px';
		el.style.height = (sw.y - ne.y) + 'px';

		self.left = sw.x;
		self.top = ne.y;
	};

	self.onRemove = function() {
		el.parentNode.removeChild(el);
	};

	return self;
}

function createSVGElement(tag, attrs) {
	var el = document.createElementNS("http://www.w3.org/2000/svg", tag);
	_.each(attrs || {}, function(v, k) {
		el.setAttributeNS(null, k, v);
	});
	return el;
}

function DensityMapType(options) {
	this.options = options;
	this.tiles = {};
	// Google maps requires these:
	this.tileSize = options.tileSize || new google.maps.Size(256, 256);
	this.maxZoom = options.maxZoom || 11;

	// Used to keep track of how many times each tile has been created and destroyed
	// This is because Google maps sometimes calls getTile() multiple times per tile: (see https://issuetracker.google.com/issues/73335429)
	this.tileCount = {};
}

DensityMapType.prototype.refresh = function() {
	var opts = this.options;

	_.each(this.tiles, function(tile, key) {
		loadTile(tile, opts);
	});
};

function increment(obs) { if(obs) obs(obs() + 1); }
function decrement(obs) { if(obs) obs(obs() - 1); }

function loadTile(tile, opts) {
	var token = {};
	var map = opts.map;

	// Clear any existing content
	while(tile.el.firstChild) {
		tile.el.removeChild(tile.el.firstChild);
	}

	tile.loading = true;

	// Increment the loading tile count if the key doesn't already exist in the request queue
	if(!requestQueue.has(tile.key)) {
		increment(opts.loadingTiles);
	}

	tile.el.token = token;

	var criteria = _.extend({summarize: "tile_density"}, opts.criteriaFn()); // density

	var recentDateThreshold = util.formatISODate(util.addDays(new Date(), -14));

	var tileFn = opts.tileFn || birdata.surveyTile;

	requestQueue.add(tile.key, function() {
		return tileFn(criteria, tile.x, tile.y, tile.z).then(function(points) {

			if(tile.el.token != token) {
				return; // Ignore stale requests
			}

			_.each(points, function(p) {
				var ll = new google.maps.LatLng(p.lat, p.lon);
				var xy = map.getProjection().fromLatLngToPoint(ll);

				xy.x *= Math.pow(2, tile.z);
				xy.y *= Math.pow(2, tile.z);

				var dx = xy.x - (tile.x * opts.tileSize.width);
				var dy = xy.y - (tile.y * opts.tileSize.height);

				var r = (Math.log(p.count) - 1) * tile.z + 1;

				var isRecent = p.last_surveyed > recentDateThreshold;

				var circle = createSVGElement("circle", {
					cx: dx * 100 / opts.tileSize.width,
					cy: dy * 100 / opts.tileSize.height,
					r: Math.max(Math.min(r / 8, 6), 1),
					'data-count': p.count,
					'data-recent': isRecent ? 'true' : 'false',
					class: (opts.highlightRecent && isRecent) ? 'recent' : ''
				});
				tile.el.appendChild(circle);
			});
		}).finally(function() {
			tile.loading = false;
			decrement(opts.loadingTiles);
		});
	});
}

DensityMapType.prototype.getTile = function(coord, zoom, ownerDocument) {
	var key = [coord.x, coord.y, zoom].join(",");

	// dummy tile div
	var div = document.createElement('div');
	div.setAttribute('data-tile', key);

	if(zoom > this.maxZoom) {
		// should never happen
		console.log("Unexpected zoom for getTile");
		return div;
	}

	// Tile book-keeping - see comments on tileCount variable
	this.tileCount[key] = this.tileCount[key] ? this.tileCount[key] + 1 : 1;
	if(this.tileCount[key] != 1) {
		return div;
	}

	increment(this.options.visibleTiles);

	var tile = {
		x: coord.x,
		y: coord.y,
		z: zoom,
		key: key
	};

	this.tiles[key] = tile;

	tile.el = createSVGElement("svg", {
		viewBox: "0 0 100 100",
		height: this.tileSize.height,
		width: this.tileSize.width,
		'class': 'density-dots',
		'data-tile': key
	});

	_.each(this.options.events || {}, function(handler, eventType) {
		tile.el.addEventListener(eventType, function(evt) {
			if(evt.target.tagName == 'circle') {
				// We have to manually figure out the
				// the mouse position and convert to lat lon.

				if(evt.offsetX) {
					var rect = tile.el.getBoundingClientRect();
					var x = (evt.clientX - rect.left) + tile.overlay.left;
					var y = (evt.clientY - rect.top) + tile.overlay.top;

					// This doesn't work in FF, because of interactions between offsetX/Y and CSS transforms
					// var x = evt.offsetX + tile.overlay.left;
					// var y = evt.offsetY + tile.overlay.top;

					var point = new google.maps.Point(x, y);
					var latLng = tile.overlay.getProjection().fromDivPixelToLatLng(point);

					handler(evt, {
						point: point,
						latLng: latLng
					});
				} else {
					handler(evt, {});
				}
			}
		});
	});

	tile.overlay = tileOverlayView(coord.x, coord.y, zoom, this.tileSize, tile.el);
	tile.overlay.setMap(this.options.map);

	loadTile(tile, this.options);

	return div;
};

DensityMapType.prototype.releaseTile = function(node) {
	var key = node.getAttribute('data-tile');

	// Tile book-keeping - see comments on tileCount variable
	this.tileCount[key] = this.tileCount[key] ? this.tileCount[key] - 1 : -1;
	if(this.tileCount[key] != 0) {
		return;
	}
	delete this.tileCount[key];

	// Decrement loading tile count if key is in the request queue
	if(requestQueue.has(key)) {
		requestQueue.remove(key);
		decrement(this.options.loadingTiles);
	}

	var tile = this.tiles[key];

	if(tile) {
		delete this.tiles[key];
		decrement(this.options.visibleTiles);

		tile.overlay.setMap(null);
	}
};

export default DensityMapType;
